<template>
  <!-- One Store-health gauge as a full-width scorecard ROW (Today layout v2). Purely
       presentational: the gauge components compute every number and hand it over, so
       the row and the dashboard's card can never disagree. Ladder rows from different
       gauges share one horizontal scale (`axisMax`), so "100% = your best weeks" sits
       at the same x in every row and the rows are comparable at a glance. -->
  <div class="hrow" :class="{ 'hrow--off': disabled }" :style="{ '--accent': accent }">
    <!-- Left: identity + the outcome figure + basis -->
    <div class="hrow-id">
      <span class="hrow-eyebrow">{{ eyebrow }}</span>
      <!-- The ⓘ explains how THIS figure is calculated → it sits with the title. -->
      <span class="hrow-subtitle">
        {{ subtitle }}
        <button v-if="help" type="button" class="hrow-help" :aria-label="$t('gauges.row.help', { name: subtitle })" @click.stop="$emit('help')">
          <i class="bi bi-question-circle"></i>
        </button>
      </span>
      <template v-if="!status || keepValue">
        <span class="hrow-value">{{ value }}<small v-if="valueUnit" class="hrow-unit">{{ valueUnit }}</small></span>
        <span class="hrow-value-sub">{{ valueSub }}<em v-if="valueEst">{{ ' ' + $t('gauges.row.est') }}</em></span>
      </template>
      <span v-if="meta" class="hrow-meta" v-html="meta"></span>
    </div>

    <!-- Right: not-configured / not-measurable statement -->
    <div v-if="status" class="hrow-status">
      <span class="hrow-status-title">{{ status.title }}</span>
      <span class="hrow-status-sub">{{ status.sub }}</span>
    </div>

    <!-- Right: the measure, on the shared grid [lead | bar | delta] -->
    <div v-else class="hrow-body">
      <div class="hrow-line">
        <div class="hrow-lead">
          <span class="hrow-pct">{{ pct }}%</span>
          <span class="hrow-pct-sub">{{ pctSub }}</span>
        </div>

        <!-- Ladder: fill vs rungs on the shared % scale -->
        <div v-if="bar === 'ladder'" class="hrow-bar">
          <div class="hrow-track">
            <div class="hrow-fill" :style="{ width: pos(pct) + '%' }"></div>
            <span
              v-for="r in placedRungs" :key="r.key"
              class="hrow-rung" :class="`rung-${r.key}`" :style="{ left: r.pos + '%' }"
            ></span>
          </div>
          <span class="hrow-best-tag" :style="{ left: pos(100) + '%' }">{{ $t('gauges.row.yourBest') }}</span>
          <div class="hrow-tags" :class="{ 'hrow-tags--two': placedTags.some(t => t.lvl) }">
            <span
              v-for="t in placedTags" :key="t.key"
              class="hrow-tag" :class="[`rung-${t.key}`, `lvl-${t.lvl}`, { beyond: t.beyond }]" :style="{ left: t.pos + '%' }"
            ><strong>{{ t.pctLabel }}</strong> {{ t.name }}<template v-if="t.beyond"> →</template></span>
          </div>
        </div>

        <!-- Split: share of paid capacity in use vs idle (its own 0–100% scale) -->
        <div v-else class="hrow-bar">
          <div class="hrow-split">
            <div class="hrow-split-use" :style="{ width: pct + '%' }"></div>
            <div class="hrow-split-idle" :style="{ width: (100 - pct) + '%' }"></div>
          </div>
          <div class="hrow-split-axis">
            <span>{{ $t('gauges.row.inUse', { pct }) }}</span>
            <span>{{ splitRightLabel }}</span>
          </div>
        </div>

        <span class="hrow-delta" :class="deltaClass">{{ delta || '' }}</span>
      </div>

      <!-- Sub-rows (e.g. other service points) — same columns, same scale -->
      <div v-for="s in subRows" :key="s.key" class="hrow-line hrow-line--sub">
        <div class="hrow-lead hrow-lead--sub">
          <span class="hrow-sub-name">{{ s.name }}</span>
          <span class="hrow-sub-text" :class="{ 'is-best': s.atBest }">{{ s.text }}</span>
          <span v-if="s.note" class="hrow-sub-note">{{ s.note }}</span>
        </div>
        <div class="hrow-bar">
          <div class="hrow-track hrow-track--sub">
            <div class="hrow-fill" :style="{ width: pos(s.pct) + '%' }"></div>
            <span
              v-for="r in s.rungs" :key="r.key"
              class="hrow-rung" :class="`rung-${r.key}`" :style="{ left: pos(r.pct) + '%' }"
            ></span>
          </div>
        </div>
        <span class="hrow-delta hrow-delta--sub">{{ s.pct }}%</span>
      </div>

      <div v-if="$slots.notes" class="hrow-notes"><slot name="notes" /></div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  eyebrow: { type: String, required: true },
  subtitle: { type: String, default: '' },
  accent: { type: String, default: '#4F7EB3' },
  disabled: { type: Boolean, default: false },
  // { title, sub } → the row shows this statement instead of a measure.
  status: { type: Object, default: null },
  // With a status: still show the value column (e.g. "1 min 35 sec — average wait now"
  // while the comparison baseline is still building).
  keepValue: { type: Boolean, default: false },
  help: { type: Boolean, default: true },       // false → no ⓘ (no explainer exists)
  value: { type: String, default: '' },
  valueSub: { type: String, default: '' },
  valueEst: { type: Boolean, default: false },
  valueUnit: { type: String, default: '' },     // e.g. '/mo' — same unit as Where to focus
  meta: { type: String, default: '' },          // trusted, component-built HTML (counts)
  pct: { type: Number, default: 0 },
  pctSub: { type: String, default: '' },
  delta: { type: String, default: null },
  bar: { type: String, default: 'ladder' },     // 'ladder' | 'split'
  // Ladder: rungs on the raw % scale [{ key: 'best'|'peers'|'bic', name, pct }].
  rungs: { type: Array, default: () => [] },
  axisMax: { type: Number, default: 100 },
  splitRightLabel: { type: String, default: '' },
  // [{ key, name, text, note?, atBest, pct, rungs: [{ key, pct }] }] — note = muted context line
  subRows: { type: Array, default: () => [] },
})
defineEmits(['help'])

const pos = (p) => Math.max(0, Math.min(100, (Number(p) / (props.axisMax || 100)) * 100))
const placedRungs = computed(() => props.rungs.map((r) => ({ ...r, pos: pos(r.pct) })))
// "your best" is labelled above the bar; peers / best-in-class below it, inline.
// Two labels closer than ~16% of the bar would overlap → the second drops a line.
const placedTags = computed(() => {
  const tags = placedRungs.value
    .filter((r) => r.key !== 'best')
    // beyond: the rung is past the end of the (capped) scale → pinned to the bar's end,
    // label right-aligned to it and marked "→", always with its true value.
    .map((r) => ({ ...r, pctLabel: `${Math.round(r.pct)}%`, beyond: r.pct > (props.axisMax || 100) }))
    .sort((a, b) => a.pos - b.pos)
  let prev = -100, prevLvl = 1
  return tags.map((t) => {
    const lvl = (t.pos - prev < 16) ? 1 - prevLvl : 0
    prev = t.pos; prevLvl = lvl
    return { ...t, lvl }
  })
})
const deltaClass = computed(() => {
  const d = props.delta || ''
  return d.startsWith('▲') ? 'is-up' : d.startsWith('▼') ? 'is-down' : ''
})
</script>

<style scoped>
.hrow {
  --accent: #4F7EB3;
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 22px;
  padding: 16px 18px 14px 16px;
  border-left: 3px solid var(--accent);
}
.hrow--off { opacity: 0.6; filter: grayscale(0.3); }

/* identity column */
.hrow-id { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.hrow-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.7px; text-transform: uppercase; color: var(--accent); }
.hrow-subtitle { font-size: 12.5px; color: #6b7a87; margin-bottom: 6px; }
.hrow-value { font-size: 22px; font-weight: 800; color: #1d2b3a; line-height: 1.1; letter-spacing: -0.3px; font-variant-numeric: tabular-nums; }
.hrow-value-sub { font-size: 12px; color: #51637a; line-height: 1.35; margin-top: 2px; }
.hrow-unit { font-size: 13px; font-weight: 600; color: #6b7a87; margin-left: 3px; letter-spacing: 0; }
.hrow-value-sub em { font-style: normal; color: #8a99a4; }
.hrow-meta { display: block; margin-top: auto; padding-top: 10px; font-size: 12px; color: #8a99a4; line-height: 1.35; }
.hrow-meta :deep(strong) { color: #6b7a87; font-weight: 600; }
.hrow-help { border: 0; background: none; padding: 0 0 0 3px; color: #9ca3af; cursor: pointer; line-height: 1; font-size: 13px; vertical-align: -1px; }
.hrow-help:hover { color: #004F6E; }

/* status (not configured / not measurable) */
.hrow-status { display: flex; flex-direction: column; justify-content: center; gap: 2px; }
.hrow-status-title { font-weight: 700; color: #51637a; font-size: 14px; }
.hrow-status-sub { font-size: 12px; color: #8a99a4; }

/* measure column — fixed lead + delta widths keep every bar at the same x */
.hrow-body { display: flex; flex-direction: column; gap: 6px; min-width: 0; justify-content: center; }
.hrow-line {
  display: grid;
  grid-template-columns: 216px minmax(0, 1fr) 96px;
  gap: 16px;
  align-items: start;
}
.hrow-lead { display: flex; flex-direction: column; gap: 0; }
.hrow-pct { font-size: 22px; font-weight: 700; color: #2b3a46; line-height: 1.05; }
.hrow-pct-sub { font-size: 12px; color: #6b7a87; line-height: 1.3; }
.hrow-delta { font-size: 12px; line-height: 1.35; color: #8a99a4; text-align: right; padding-top: 16px; }
.hrow-delta.is-up { color: #2c6f58; }
.hrow-delta.is-down { color: #a0521c; }

/* ladder bar */
.hrow-bar { position: relative; padding-top: 20px; min-width: 0; }
.hrow-track { position: relative; height: 12px; border-radius: 7px; background: #eef2f7; }
.hrow-fill {
  position: absolute; left: 0; top: 0; bottom: 0;
  background: var(--accent); border-radius: 7px 3px 3px 7px; transition: width .3s ease;
}
.hrow-rung { position: absolute; top: -4px; bottom: -4px; width: 2px; transform: translateX(-1px); border-radius: 2px; }
.hrow-rung.rung-best  { background: #2b3a46; width: 3px; top: -6px; bottom: -6px; }
.hrow-rung.rung-peers { background: #8fa2b7; }
.hrow-rung.rung-bic   { background: #b9c4d0; }
.hrow-best-tag {
  position: absolute; top: 0; transform: translateX(-50%);
  font-size: 11px; font-weight: 600; color: #51637a; white-space: nowrap;
}
.hrow-tags { position: relative; height: 16px; margin-top: 5px; }
.hrow-tags--two { height: 32px; }
.hrow-tag {
  position: absolute; transform: translateX(-50%); white-space: nowrap;
  font-size: 11px; color: #8a99a4; line-height: 1.3;
}
.hrow-tag strong { color: #51637a; font-weight: 600; }
.hrow-tag.lvl-1 { top: 15px; }
.hrow-tag.beyond { transform: translateX(-100%); }

/* sub-rows (other service points) */
.hrow-line--sub { align-items: center; }
.hrow-line--sub .hrow-bar { padding-top: 0; }
.hrow-lead--sub { flex-direction: column; gap: 0; padding-left: 10px; border-left: 2px solid #e6ebd2; }
.hrow-sub-name { font-size: 12px; font-weight: 600; color: #2b3a46; white-space: nowrap; }
.hrow-sub-text { font-size: 12px; color: #6b7a87; line-height: 1.3; }
.hrow-sub-text.is-best { color: #2c6f58; }
.hrow-sub-note { font-size: 12px; color: #8a99a4; line-height: 1.3; }
.hrow-track--sub { height: 6px; }
.hrow-track--sub .hrow-fill { opacity: .75; }
.hrow-track--sub .hrow-rung { top: -3px; bottom: -3px; }
.hrow-delta--sub { padding-top: 0; color: #6b7a87; white-space: nowrap; }

/* split bar (DEPLOY) — deliberately a different form: its own 0–100% scale */
.hrow-split { display: flex; height: 12px; border-radius: 7px; overflow: hidden; background: #eef2f7; }
.hrow-split-use { background: var(--accent); }
.hrow-split-idle {
  background: repeating-linear-gradient(135deg, #e7ecf3 0 5px, #f5f7fa 5px 10px);
}
.hrow-split-axis { display: flex; justify-content: space-between; font-size: 12px; color: #8a99a4; margin-top: 5px; }

/* notes (at-risk, idle-when) — under the bar column */
.hrow-notes { padding-left: 232px; padding-right: 112px; font-size: 12px; color: #51637a; }

@media (max-width: 900px) {
  .hrow { grid-template-columns: 1fr; gap: 10px; }
  .hrow-line { grid-template-columns: 110px minmax(0, 1fr) 64px; gap: 10px; }
  .hrow-notes { padding: 0; }
}
</style>
